import { useQuery } from "@tanstack/react-query"
import { FilterMenu, FilterMenuField } from "design-system/molecules/FilterMenu"
import { useRecalculateUnansweredQuestionTopics } from "modules/backoffice/lib/knowledge/useRecalculateUnansweredQuestionTopics"
import { CompanySelector } from "modules/backoffice/components/CompanySelector"
import { BACKOFFICE_RECENT_TIME_FRAME_OPTIONS } from "modules/backoffice/lib/backofficeRecentTimeFrameOptions"
import { getBackofficeKnowledgeCapabilitiesQuery } from "modules/backoffice/lib/knowledge/getBackofficeKnowledgeCapabilitiesQuery"
import { getBackofficeKnowledgeCategoriesQuery } from "modules/backoffice/lib/knowledge/getBackofficeKnowledgeCategoriesQuery"
import { getBackofficeKnowledgeItemsQuery } from "modules/backoffice/lib/knowledge/getBackofficeKnowledgeItemsQuery"
import { getBackofficeKnowledgeOverviewQuery } from "modules/backoffice/lib/knowledge/getBackofficeKnowledgeOverviewQuery"
import { getBackofficeKnowledgeTimelineQuery } from "modules/backoffice/lib/knowledge/getBackofficeKnowledgeTimelineQuery"
import { TimeRangeSelector } from "modules/shared/components/TimeRange/TimeRangeSelector"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { useTranslation } from "react-i18next"
import { Route } from "routes/_base/_auth/backoffice/knowledge/route"
import { BackofficeKnowledgeFiltersSchema, OrganizationTypeEnum } from "schema"
import { BackofficeKnowledgeAnswerSources } from "./BackofficeKnowledgeAnswerSources"
import { BackofficeKnowledgeCapabilitiesTable } from "./BackofficeKnowledgeCapabilitiesTable"
import { BackofficeKnowledgeCategoriesTable } from "./BackofficeKnowledgeCategoriesTable"
import { BackofficeKnowledgeCompaniesTable } from "./BackofficeKnowledgeCompaniesTable"
import { BackofficeKnowledgeHeadline } from "./BackofficeKnowledgeHeadline"
import { BackofficeKnowledgeItemsTable } from "./BackofficeKnowledgeItemsTable"
import { BackofficeKnowledgeTimeline } from "./BackofficeKnowledgeTimeline"

export const BackofficeKnowledge = () => {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { t } = useTranslation()
  const labels = useFilterBarLabels()
  const { timeFrame, companyId, questionPosition, onlyShopify, onlyClients } = search
  const recalculation = useRecalculateUnansweredQuestionTopics()
  const metricsQuery = { timeFrame, companyId, questionPosition, onlyShopify, onlyClients }

  const { data: overview, isPending: isOverviewPending } = useQuery(
    getBackofficeKnowledgeOverviewQuery({ query: metricsQuery }),
  )
  const { data: timeline, isPending: isTimelinePending } = useQuery(
    getBackofficeKnowledgeTimelineQuery({ query: metricsQuery }),
  )
  const { data: categories, isPending: isCategoriesPending } = useQuery(
    getBackofficeKnowledgeCategoriesQuery({ query: metricsQuery }),
  )
  const { data: items, isPending: isItemsPending } = useQuery(
    getBackofficeKnowledgeItemsQuery({ query: metricsQuery }),
  )
  const { data: capabilities, isPending: isCapabilitiesPending } = useQuery(
    getBackofficeKnowledgeCapabilitiesQuery({ query: metricsQuery }),
  )

  function updateSearch(next: Partial<typeof search>) {
    navigate({ search: { ...search, ...next } })
  }

  const fields: FilterMenuField[] = [
    {
      key: "onlyShopify",
      label: t("backoffice.knowledge.filters.onlyShopify"),
      value: onlyShopify ? "true" : undefined,
      onChange: (value) => updateSearch({ onlyShopify: value === "true", companyId: undefined }),
      options: [{ value: "true", label: t("shared.filterBar.yes") }],
    },
    {
      key: "onlyClients",
      label: t("backoffice.knowledge.filters.onlyClients"),
      value: onlyClients ? "true" : undefined,
      onChange: (value) => updateSearch({ onlyClients: value === "true", companyId: undefined }),
      options: [{ value: "true", label: t("shared.filterBar.yes") }],
    },
    {
      key: "questionPosition",
      label: t("backoffice.knowledge.filters.questionPosition"),
      value: questionPosition === "all" ? undefined : questionPosition,
      onChange: (value) =>
        updateSearch({
          questionPosition: BackofficeKnowledgeFiltersSchema.shape.questionPosition.parse(
            value ?? "all",
          ),
        }),
      options: ["first", "second", "thirdPlus"].map((value) => ({
        value,
        label: t(`backoffice.knowledge.filters.positions.${value}`),
      })),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
        <CompanySelector
          value={companyId}
          onChange={(nextCompanyId) => updateSearch({ companyId: nextCompanyId })}
          hasShopifyIntegration={onlyShopify}
          organizationTypes={
            onlyClients
              ? [OrganizationTypeEnum.Client, OrganizationTypeEnum.Partner]
              : Object.values(OrganizationTypeEnum)
          }
          className="h-10 w-64 max-w-full"
        />
        <TimeRangeSelector
          value={timeFrame}
          onChange={({ timeFrame: nextTimeFrame }) => updateSearch({ timeFrame: nextTimeFrame })}
          presets={BACKOFFICE_RECENT_TIME_FRAME_OPTIONS}
        />
        <FilterMenu fields={fields} labels={labels} />
      </div>
      <BackofficeKnowledgeHeadline total={overview?.total} isPending={isOverviewPending} />
      <BackofficeKnowledgeAnswerSources total={overview?.total} isPending={isOverviewPending} />
      <BackofficeKnowledgeTimeline data={timeline} isPending={isTimelinePending} />
      <BackofficeKnowledgeCategoriesTable
        key={`categories:${JSON.stringify(metricsQuery)}`}
        data={categories}
        isPending={isCategoriesPending}
        query={metricsQuery}
      />
      <BackofficeKnowledgeCompaniesTable
        key={`companies:${JSON.stringify(metricsQuery)}`}
        data={overview?.companies}
        isPending={isOverviewPending}
        isRecalculateDisabled={recalculation.isPending || recalculation.isSuccess}
        onRecalculate={() => recalculation.mutate()}
      />
      {companyId && <BackofficeKnowledgeItemsTable data={items} isPending={isItemsPending} />}
      {companyId && (
        <BackofficeKnowledgeCapabilitiesTable
          data={capabilities}
          isPending={isCapabilitiesPending}
        />
      )}
    </div>
  )
}
